import { InfinityIcon } from "@images/svgIcons/infinityIcon"
import { OnOffIcon } from "@images/svgIcons/onOffIcon"
import { word } from "@modules/core/utils/i18n"
import { ProgramBundleEntity } from "@modules/programBundle/entities/programBundleEntity"
import { ProgramInfinite, ProgramInfiniteTypeOf } from "@modules/programs/programInfiniteTypes"
import { ProgramOnOff } from "@modules/programs/programOnOffTypes"
import { ProgramSubscriberDetailsHeader } from "@modules/programs/programSubscriber/components/programSubscriberDetails/programSubscriberDetailsHeader"
import { ProgramSubscriberDetailsWrapper } from "@modules/programs/programSubscriber/components/programSubscriberDetails/programSubscriberDetailsWrapper"
import { HuAvatarCardSidebarSkeleton } from "@modules/ui/components/huAvatarCardSidebarSkeleton"
import { HUText } from "@modules/ui/components/huText"
import { BoxesIcon } from "lucide-react"
import { Suspense, useMemo } from "react"
import { useTheme } from "styled-components"
import { ErrorBoundary } from "react-error-boundary"
import { ProgramSubscriberDetailsContentSkeleton } from "@modules/programs/programSubscriber/components/programSubscriberDetails/progamSubscriberDetailsContentSkeleton"
import { ProgramSubscriberDetailsLayoutError } from "@modules/programs/programSubscriber/components/programSubscriberDetails/programSubscriberDetailsLayoutError"

type ProgramSubscriberDetailsLayoutProps = {
  program: ProgramInfinite | ProgramOnOff | ProgramBundleEntity
  subscriberId: string
  className?: string
} & React.ComponentProps<"div">

export function ProgramSubscriberDetailsLayout({
  program,
  subscriberId,
  className,
  ...props
}: ProgramSubscriberDetailsLayoutProps) {
  const defaultClassName = "flex flex-column w-full gap-4"
  const theme = useTheme()

  const itemIcon = useMemo(() => {
    if (program instanceof ProgramBundleEntity) {
      return <BoxesIcon color={theme.neutralColor500} width={15} height={15} />
    } else if (program._programType === ProgramInfiniteTypeOf) {
      return <InfinityIcon color={theme.neutralColor500} width={15} height={15} />
    } else return <OnOffIcon color={theme.neutralColor500} width={15} height={15} />
  }, [program])

  return (
    <div className={className ?? defaultClassName} {...props}>
      <ErrorBoundary fallback={<ProgramSubscriberDetailsLayoutError />}>
        {/* HEADER */}
        <Suspense
          fallback={
            <HuAvatarCardSidebarSkeleton
              topContent={
                <div className="flex flex-column align-items-center pt-2">
                  <HUText fontStyle="TM">{word("program.label.mixed_subscribers")}</HUText>
                  <HUText
                    fontStyle="LM"
                    color={theme.neutralColor500}
                    className="capitalize flex gap-1 align-items-center"
                  >
                    {itemIcon}
                    {program instanceof ProgramBundleEntity ? program.name : program.title}
                  </HUText>
                </div>
              }
            />
          }
        >
          <ProgramSubscriberDetailsHeader program={program} subscriberId={subscriberId} />
        </Suspense>

        {/* CONTENT */}
        <Suspense fallback={<ProgramSubscriberDetailsContentSkeleton />}>
          <ProgramSubscriberDetailsWrapper program={program} subscriberId={subscriberId} />
        </Suspense>
      </ErrorBoundary>
    </div>
  )
}
